import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { getLocalDateString, getYesterdayDateString } from '../utils/dateUtils';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import FatSecretBadge from '../components/FatSecretBadge';
import PremiumIcon from '../components/PremiumIcon';
import RenameMealDialog from '../components/meals/RenameMealDialog';
import CopyMealToDateDialog from '../components/meals/CopyMealToDateDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { 
  Loader2, 
  UtensilsCrossed, 
  ChefHat, 
  AlertTriangle, 
  CheckCircle2, 
  Trash2, 
  Edit3, 
  Save, 
  X,
  MoreVertical,
  Plus,
  Sparkles,
  ChevronDown,
  ChevronRight,
  Calendar,
  Heart,
  RefreshCw,
  CalendarDays,
  Search,
  Pencil,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

/**
 * Safe number coercion — accepts any value (number, string, null, undefined)
 * and returns a finite Number. Critical for any *.toFixed() call below
 * because some legacy meals in DB may have numeric fields stored as strings
 * (the LLM occasionally returned them as strings before the v138 backend
 * coercion patch). Without this, the Meals page would crash on those rows.
 */
const safeNumber = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

export const Meals = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Assistant IA deep-link bridge: forward ?action=describe&text=... to /dashboard
  // where the meal-add modals live (DRY: no duplicate modal logic here).
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.get('action')) {
      navigate({ pathname: '/dashboard', search: location.search }, { replace: true });
    }
    // Iter. 406.fr.73 — `?quickLog=protein` used to auto-inject 30 g P
    // when the user tapped "AGIR" on the pillar alert bubble. The bubble is
    // now purely informational — no more silent macros mutations. We simply
    // strip the query so a stale URL from a previous version doesn't fire.
    if (sp.get('quickLog')) {
      navigate({ pathname: '/meals', search: '' }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [mealToDelete, setMealToDelete] = useState(null);
  // Iter. 402 — Inline meal rename dialog (custom_name for history entries).
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [mealToRename, setMealToRename] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [savingMealRename, setSavingMealRename] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editIngredients, setEditIngredients] = useState([]);
  const [recalculating, setRecalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [expandedDays, setExpandedDays] = useState({});
  // Favorites section
  const [favorites, setFavorites] = useState([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [favoritesExpanded, setFavoritesExpanded] = useState(false);
  const [favoritesSearch, setFavoritesSearch] = useState('');
  const [addingFavoriteId, setAddingFavoriteId] = useState(null);
  // Iter. 396 · Lot #12 — Inline rename state (one favorite at a time).
  const [editingFavBarcode, setEditingFavBarcode] = useState(null);
  const [editFavValue, setEditFavValue] = useState('');
  const [savingFavRename, setSavingFavRename] = useState(false);
  // Duplicate-to-another-date
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [copyDialogMeal, setCopyDialogMeal] = useState(null);
  const [copyTargetDate, setCopyTargetDate] = useState('');
  const [copySaving, setCopySaving] = useState(false);
  // User's daily protein target — fetched once, reused on each day-header bar
  // to render the orange-glow Micro-Victory signature when a day clears the
  // floor (e.g., 112g for a 165 lb client).
  const [proteinTargetMin, setProteinTargetMin] = useState(0);

  // Iter. 403 — Server-side full-text search across `custom_name` and
  // `foods_detected[].name`. Debounced; bumps the limit so matches deep in
  // history surface in a single round-trip.
  const [historySearch, setHistorySearch] = useState('');
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (user) {
      // Iter. 403 — Initial `fetchMeals()` is now driven by the debounced
      // search effect below (fires with empty search on mount).
      fetchFavorites();
      // Fire and forget — protein target is non-blocking.
      api.get('/dashboard/stats').then(r => {
        const v = r?.data?.protein_target_min;
        if (typeof v === 'number' && v > 0) setProteinTargetMin(v);
      }).catch(() => {});
    }
  }, [user]);

  const fetchFavorites = async () => {
    setFavoritesLoading(true);
    try {
      const res = await api.get('/scan/favorites');
      setFavorites(res.data.favorites || []);
    } catch {
      // silent: favorites section just stays empty
    } finally {
      setFavoritesLoading(false);
    }
  };

  const logFavoriteAsMeal = async (fav) => {
    if (addingFavoriteId) return;
    setAddingFavoriteId(fav.id || fav.barcode);
    try {
      await api.post(`/scan/favorites/${encodeURIComponent(fav.barcode)}/log-meal`);
      toast.success(t('toast.meal_full_saved'));
      await fetchMeals();
    } catch {
      toast.error(t('common.error'));
    } finally {
      setAddingFavoriteId(null);
    }
  };

  const deleteFavorite = async (fav) => {
    try {
      await api.delete(`/scan/favorites/${encodeURIComponent(fav.barcode)}`);
      setFavorites(prev => prev.filter(f => f.barcode !== fav.barcode));
      toast.success(t('toast.deleted'));
    } catch {
      toast.error(t('meals_page.erreur', 'Erreur'));
    }
  };

  // Iter. 396 · Lot #12 — Rename a favorite inline.
  // Empty / whitespace-only input → unset the override (auto name restored).
  const startEditFav = (fav) => {
    setEditingFavBarcode(fav.barcode);
    setEditFavValue(fav.custom_name || fav.product_name || '');
  };
  const cancelEditFav = () => {
    setEditingFavBarcode(null);
    setEditFavValue('');
  };
  const commitEditFav = async () => {
    if (!editingFavBarcode) { cancelEditFav(); return; }
    setSavingFavRename(true);
    try {
      const trimmed = editFavValue.trim();
      const res = await api.patch(
        `/scan/favorites/${encodeURIComponent(editingFavBarcode)}/rename`,
        { custom_name: trimmed || null }
      );
      const newName = res?.data?.custom_name || null;
      setFavorites(prev => prev.map(f => (
        f.barcode === editingFavBarcode ? { ...f, custom_name: newName } : f
      )));
      toast.success(
        newName
          ? (language === 'fr' ? '✏️ Favori renommé' : language === 'es' ? '✏️ Favorito renombrado' : '✏️ Favorite renamed')
          : (language === 'fr' ? '↩️ Nom restauré' : language === 'es' ? '↩️ Nombre restaurado' : '↩️ Name restored'),
        { duration: 2200 }
      );
      cancelEditFav();
    } catch (err) {
      toast.error(
        language === 'fr' ? 'Erreur lors du renommage' :
        language === 'es' ? 'Error al renombrar' : 'Rename failed'
      );
    } finally {
      setSavingFavRename(false);
    }
  };

  const duplicateMealToday = async (meal) => {
    if (duplicatingId) return;
    setDuplicatingId(meal.meal_id);
    try {
      const res = await api.post(`/meals/${meal.meal_id}/duplicate`, {});
      toast.success(t('meals_page.relogged_today', "Re-ajouté à aujourd'hui !"));
      // Prepend the new meal to the list and expand today
      const today = getLocalDateString();
      setMeals(prev => [res.data.meal, ...prev]);
      setTotal(prev => prev + 1);
      setExpandedDays(prev => ({ ...prev, [today]: true }));
    } catch (err) {
      // Surface the real backend error so users can report what they see.
      // The silent "Erreur duplication" mask made Tetout's bug impossible to
      // diagnose — 2026-05-01 fix.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || '';
      const networkIssue = !err?.response || err?.code === 'ECONNABORTED' || String(err?.message || '').toLowerCase().includes('network');
      const msg = networkIssue
        ? (t('meals_page.connexion_instable_reessaie', 'Connexion instable. Réessaie.'))
        : status === 404
          ? (t('meals_page.ce_repas_a_ete_supprime_rafraichis_la_li', 'Ce repas a été supprimé. Rafraîchis la liste.'))
          : detail
            ? `${t('meals_page.erreur', 'Erreur')} ${status || ''} · ${String(detail).slice(0, 80)}`
            : (t('meals_page.erreur_duplication', 'Erreur duplication'));
      toast.error(msg);
      console.error('[Meals] duplicate failed', { status, detail, err });
    } finally {
      setDuplicatingId(null);
    }
  };

  const openCopyDialog = (meal) => {
    setCopyDialogMeal(meal);
    setCopyTargetDate(getLocalDateString());
  };

  const handleCopyToDate = async () => {
    if (!copyDialogMeal || !copyTargetDate) return;
    setCopySaving(true);
    try {
      const res = await api.post(`/meals/${copyDialogMeal.meal_id}/duplicate`, {
        target_date: copyTargetDate,
      });
      setMeals(prev => [res.data.meal, ...prev]);
      setTotal(prev => prev + 1);
      setExpandedDays(prev => ({ ...prev, [copyTargetDate]: true }));
      toast.success(t('meals_page.copied_to_date', 'Copié au {{date}}', { date: copyTargetDate }));
      setCopyDialogMeal(null);
    } catch {
      toast.error(t('meals_page.erreur_copie', 'Erreur copie'));
    } finally {
      setCopySaving(false);
    }
  };

  // Build a stable barcode for a meal so favorites are idempotent per-meal.
  const mealFavoriteBarcode = (meal) => `meal_${meal.meal_id}`;
  const isMealFavorited = (meal) => favorites.some(f => f.barcode === mealFavoriteBarcode(meal));

  const saveMealAsFavorite = async (meal) => {
    const barcode = mealFavoriteBarcode(meal);
    if (isMealFavorited(meal)) {
      toast.info(t('meals_page.deja_dans_tes_favoris', 'Déjà dans tes favoris'));
      return;
    }
    const firstFood = meal.foods_detected?.[0]?.name;
    const productName = firstFood || (t('meals_page.mon_repas', 'Mon repas'));
    try {
      await api.post('/scan/favorites', {
        barcode,
        product_name: productName,
        brand: '',
        image_url: '',
        nutrition_per_100g: {
          calories: safeNumber(meal.total_calories),
          protein: safeNumber(meal.total_protein_g),
          carbs: meal.total_carbs_g || 0,
          fat: meal.total_fats_g || 0,
          fiber: meal.total_fiber_g || 0,
        },
      });
      toast.success(t('meals_page.ajoute_aux_favoris', 'Ajoute aux favoris !'));
      // Refresh the favorites list so the new entry appears instantly
      await fetchFavorites();
      setFavoritesExpanded(true);
    } catch {
      toast.error(t('meals_page.erreur_sauvegarde_favori', 'Erreur sauvegarde favori'));
    }
  };

  const fetchMeals = async (searchTerm) => {
    try {
      const q = (searchTerm ?? historySearch ?? '').trim();
      const url = q
        ? `/meals?limit=200&search=${encodeURIComponent(q)}`
        : '/meals?limit=100';
      const response = await api.get(url);
      setMeals(response.data.meals);
      setTotal(response.data.total);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || (t('meals_page.erreur_de_chargement_des_repas', 'Erreur de chargement des repas')));
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  // Iter. 403 — Debounced refetch on search change. Auto-expands all matching
  // days when the user is searching so they don't have to manually open each.
  useEffect(() => {
    if (!user) return;
    setSearching(true);
    const timer = setTimeout(() => {
      fetchMeals(historySearch);
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historySearch, user]);

  // Group meals by date
  const groupMealsByDate = () => {
    const groups = {};
    meals.forEach(meal => {
      try {
        const date = new Date(meal.analysis_date);
        if (isNaN(date.getTime())) return; // skip invalid dates
        // CRITICAL: Use LOCAL date key (not UTC) — meal at 22h30 local stays in "today"
        const key = getLocalDateString(date);
        if (!groups[key]) groups[key] = { date, meals: [] };
        groups[key].meals.push(meal);
      } catch {
        // skip invalid entries
      }
    });
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([key, val]) => ({ dateKey: key, date: val.date, meals: val.meals }));
  };

  const formatDayLabel = (dateKey) => {
    // CRITICAL: Use LOCAL date (not UTC) for today/yesterday comparison
    const today = getLocalDateString();
    const yesterday = getYesterdayDateString();
    const date = new Date(dateKey + 'T12:00:00');
    const dayName = date.toLocaleDateString(t('meals_page.fr_fr', 'fr-FR'), { weekday: 'long' });
    const dayMonth = date.toLocaleDateString(t('meals_page.fr_fr', 'fr-FR'), { day: 'numeric', month: 'long' });

    if (dateKey === today) return t('meals_page.today_dm', "Aujourd'hui, {{dm}}", { dm: dayMonth });
    if (dateKey === yesterday) return t('meals_page.yesterday_dm', 'Hier, {{dm}}', { dm: dayMonth });
    return `${dayName.charAt(0).toUpperCase() + dayName.slice(1)}, ${dayMonth}`;
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(t('meals_page.fr_fr', 'fr-FR'), { hour: '2-digit', minute: '2-digit' });
  };

  // Auto-expand today on first load
  useEffect(() => {
    if (meals.length > 0) {
      const today = getLocalDateString();
      setExpandedDays(prev => {
        if (Object.keys(prev).length === 0) return { [today]: true };
        return prev;
      });
    }
  }, [meals]);

  const toggleDay = (dateKey) => {
    setExpandedDays(prev => ({ ...prev, [dateKey]: !prev[dateKey] }));
  };

  const getDayTotals = (dayMeals) => {
    return dayMeals.reduce((acc, m) => ({
      protein: acc.protein + safeNumber(m.total_protein_g),
      calories: acc.calories + safeNumber(m.total_calories),
      carbs: acc.carbs + (m.total_carbs_g || 0),
    }), { protein: 0, calories: 0, carbs: 0 });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'on_track': return 'text-primary border-primary/30 bg-primary/10';
      case 'low': return 'text-status-warning border-status-warning/30 bg-status-warning/10';
      case 'high': return 'text-destructive border-destructive/30 bg-destructive/10';
      default: return 'text-muted-foreground border-border bg-secondary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'on_track': return <CheckCircle2 className="w-3.5 h-3.5" />;
      case 'low': case 'high': return <AlertTriangle className="w-3.5 h-3.5" />;
      default: return <UtensilsCrossed className="w-3.5 h-3.5" />;
    }
  };

  const openEditDialog = (meal) => {
    setSelectedMeal(meal);
    setEditForm({
      total_protein_g: safeNumber(meal.total_protein_g),
      total_calories: safeNumber(meal.total_calories),
      total_carbs_g: safeNumber(meal.total_carbs_g),
      total_fats_g: safeNumber(meal.total_fats_g),
      total_fiber_g: safeNumber(meal.total_fiber_g),
      meal_type: meal.meal_type || 'repas',
    });
    setEditIngredients(
      (meal.foods_detected || []).map((f, i) => ({
        id: i, name: f.name || '', portion_g: f.portion_g || 100,
        unit: f.unit || 'g',
        protein_g: f.protein_g || 0, calories: f.calories || 0,
        carbs_g: f.carbs_g || 0, fats_g: f.fats_g || 0, fiber_g: f.fiber_g || 0,
      }))
    );
    setShowEditDialog(true);
  };

  // Iter. 406.fr.75 — Unit toggle helper for the inline "Mes repas" edit modal.
  // Cycles g → ml → piece. Macros are NOT recomputed here — the user must tap
  // "Recalculer macros (IA)" afterwards to get accurate values for the new unit.
  const cycleIngredientUnit = (idx) => {
    const CYCLE = ['g', 'ml', 'piece'];
    setEditIngredients(prev => prev.map((ing, i) => {
      if (i !== idx) return ing;
      const current = ing.unit || 'g';
      const nextIdx = (CYCLE.indexOf(current) + 1) % CYCLE.length;
      return { ...ing, unit: CYCLE[nextIdx] };
    }));
  };
  const unitLabel = (u) => ({ g: 'g', ml: 'ml', piece: 'pce' }[u || 'g'] || 'g');

  const handleRecalculateAI = async () => {
    if (editIngredients.length === 0) return;
    setRecalculating(true);
    try {
      const res = await api.post('/meals/recalculate-ingredients', {
        ingredients: editIngredients.map(i => ({ name: i.name, portion_g: i.portion_g, unit: i.unit || 'g' }))
      });
      if (res.data.success) {
        setEditForm(res.data.totals);
        const updatedFoods = res.data.foods || [];
        setEditIngredients(prev => prev.map((ing, idx) => ({ ...ing, ...(updatedFoods[idx] || {}), id: ing.id })));
        toast.success(t('meals_page.macros_recalculees_par_l_ia', 'Macros recalculees par l\\\'IA!'));
      }
    } catch { toast.error(t('meals_page.erreur_de_recalcul', 'Erreur de recalcul')); }
    finally { setRecalculating(false); }
  };

  const handleSaveEdit = async () => {
    if (!selectedMeal) return;
    setSaving(true);
    try {
      const payload = { ...editForm, foods_detected: editIngredients.map(i => ({ name: i.name, portion_g: i.portion_g, unit: i.unit || 'g', protein_g: i.protein_g, calories: i.calories, carbs_g: i.carbs_g, fats_g: i.fats_g, fiber_g: i.fiber_g })) };
      await api.patch(`/meals/${selectedMeal.meal_id}`, payload);
      toast.success(t('meals_page.repas_modifie', 'Repas modifie!'));
      setMeals(prev => prev.map(m => m.meal_id === selectedMeal.meal_id ? { ...m, ...editForm, foods_detected: payload.foods_detected, manually_edited: true } : m));
      setShowEditDialog(false);
    } catch { toast.error(t('meals_page.erreur_de_modification', 'Erreur de modification')); }
    finally { setSaving(false); }
  };

  const openDeleteDialog = (meal) => { setMealToDelete(meal); setShowDeleteDialog(true); };

  // Iter. 402 — Rename a history meal (custom_name).
  const openRenameDialog = (meal) => {
    setMealToRename(meal);
    setRenameValue(meal.custom_name || '');
    setShowRenameDialog(true);
  };
  const handleSaveRename = async () => {
    if (!mealToRename) return;
    setSavingMealRename(true);
    try {
      const trimmed = renameValue.trim();
      // Iter. 405.f — backend's unset path triggers on empty string, NOT
      // on null (cf. /app/backend/routes/meals.py "if data.custom_name
      // is not None" guard). Send '' to clear the custom label.
      await api.patch(`/meals/${mealToRename.meal_id}`, { custom_name: trimmed || '' });
      setMeals(prev => prev.map(m => (
        m.meal_id === mealToRename.meal_id
          ? { ...m, custom_name: trimmed || null }
          : m
      )));
      toast.success(
        trimmed
          ? (language === 'fr' ? '✏️ Repas renommé' : language === 'es' ? '✏️ Comida renombrada' : '✏️ Meal renamed')
          : (language === 'fr' ? '↩️ Nom auto restauré' : language === 'es' ? '↩️ Nombre auto restaurado' : '↩️ Auto name restored'),
        { duration: 2200 }
      );
      setShowRenameDialog(false);
      setMealToRename(null);
      setRenameValue('');
    } catch {
      toast.error(language === 'fr' ? 'Erreur de renommage' : language === 'es' ? 'Error al renombrar' : 'Rename failed');
    } finally {
      setSavingMealRename(false);
    }
  };

  const handleDeleteMeal = async () => {
    if (!mealToDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/meals/${mealToDelete.meal_id}`);
      toast.success(t('meals_page.repas_supprime', 'Repas supprime!'));
      setMeals(prev => prev.filter(m => m.meal_id !== mealToDelete.meal_id));
      setTotal(prev => prev - 1);
      setShowDeleteDialog(false); setMealToDelete(null);
    } catch { toast.error(t('meals_page.erreur_de_suppression', 'Erreur de suppression')); }
    finally { setDeleting(false); }
  };

  // ── Opération Vitesse: lazy-load day groups ──
  // Render only the first N day-groups synchronously, then reveal more as the
  // user scrolls close to the end. Keeps the first paint light even when the
  // user has hundreds of meals logged historically.
  // NOTE: hooks MUST be declared before any early return (Rules of Hooks).
  const LAZY_PAGE_SIZE = 7;
  const groupedMeals = loading ? [] : groupMealsByDate();
  const [visibleDays, setVisibleDays] = useState(LAZY_PAGE_SIZE);
  const lazySentinelRef = useRef(null);
  useEffect(() => {
    setVisibleDays(LAZY_PAGE_SIZE); // reset when filters / meals change
  }, [meals.length]);
  useEffect(() => {
    const node = lazySentinelRef.current;
    if (!node || visibleDays >= groupedMeals.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisibleDays((n) => Math.min(n + LAZY_PAGE_SIZE, groupedMeals.length));
        }
      },
      { rootMargin: '300px' },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [visibleDays, groupedMeals.length]);
  const displayedMeals = groupedMeals.slice(0, visibleDays);
  const isSearching = historySearch.trim().length > 0;

  // Iter. 403 — Auto-expand all matching day groups while a search is active
  // so the user instantly sees the meals that matched (no manual day-tap).
  useEffect(() => {
    if (!isSearching || groupedMeals.length === 0) return;
    setExpandedDays((prev) => {
      const next = { ...prev };
      for (const g of groupedMeals) next[g.dateKey] = true;
      return next;
    });
    if (visibleDays < groupedMeals.length) {
      setVisibleDays(groupedMeals.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearching, groupedMeals.length]);

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-[80vh]"><Loader2 className="w-12 h-12 text-primary animate-spin" /></div></Layout>;
  }

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-3xl mx-auto" data-testid="meals-page">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-foreground mb-1">
            {t('nav.meals')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {total} {t('meals_page.repas_enregistres', 'repas enregistres')}
          </p>
        </motion.div>

        {/* ─── Rattrapage repas oublié — Phase 1 (2026-04-29) ───
            Routes back to Dashboard with the meal-add modal pre-opened and
            date pre-set to yesterday (J-1). User can change the date inline
            via the discreet date row at the top of AddMealOptionsModal. */}
        <button
          onClick={() => {
            const yest = new Date();
            yest.setDate(yest.getDate() - 1);
            const y = yest.getFullYear();
            const m = String(yest.getMonth() + 1).padStart(2, '0');
            const d = String(yest.getDate()).padStart(2, '0');
            navigate('/dashboard', {
              state: { openMealAdd: true, prefillDate: `${y}-${m}-${d}`, dateFocus: true },
            });
          }}
          className="w-full mb-5 flex items-center justify-between gap-3 p-3.5 rounded-xl transition-all active:scale-[0.99]"
          data-testid="meals-backfill-cta"
          style={{
            background: 'linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(251,146,60,0.22) 100%)',
            border: '1px solid rgba(251,146,60,0.5)',
            boxShadow: '0 0 14px rgba(251,146,60,0.4), inset 0 1px 0 rgba(255,255,255,0.06)',
            backdropFilter: 'blur(6px)',
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <PremiumIcon glyph="📅" tone="orange" size="md" data-testid="meals-backfill-icon" />
            <div className="text-left min-w-0">
              <p
                className="text-sm font-black truncate uppercase tracking-wider"
                style={{ color: '#fed7aa', textShadow: '0 0 6px rgba(251,146,60,0.6)' }}
              >
                {t('meals_page.rattraper_un_repas', 'Rattraper un repas')}
              </p>
              <p className="text-[11px] truncate" style={{ color: 'rgba(251,146,60,0.75)' }}>
                {t('meals_page.photo_etiquette_ou_description_pour_une_', 'Photo, étiquette ou description — pour une journée passée (jusqu\\\'à 30j)')}
              </p>
            </div>
          </div>
          <Plus
            className="w-5 h-5 flex-shrink-0"
            style={{ color: '#fed7aa', filter: 'drop-shadow(0 0 6px rgba(251,146,60,0.9))' }}
          />
        </button>

        {/* ─── Mes Favoris — GLOW aesthetic (Iter. 411.bb) ─── */}
        <div
          className="mb-5 rounded-xl overflow-hidden"
          data-testid="favorites-section"
          style={{
            background: 'linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(236,72,153,0.18) 100%)',
            border: '1px solid rgba(236,72,153,0.45)',
            boxShadow: '0 0 14px rgba(236,72,153,0.35), inset 0 1px 0 rgba(255,255,255,0.05)',
            backdropFilter: 'blur(6px)',
          }}
        >
          <button
            onClick={() => setFavoritesExpanded(v => !v)}
            className="w-full flex items-center justify-between p-3 sm:p-4 transition-colors active:scale-[0.99]"
            data-testid="favorites-toggle"
          >
            <div className="flex items-center gap-2.5">
              <PremiumIcon glyph="❤️" tone="rose" size="xs" />
              <span
                className="font-heading text-sm uppercase tracking-widest font-black"
                style={{ color: '#fbcfe8', textShadow: '0 0 6px rgba(236,72,153,0.6)' }}
              >
                {t('meals_page.mes_favoris', 'Mes Favoris')}
              </span>
              <span
                className="text-[10px] px-1.5 py-0.5 rounded-full font-black"
                style={{
                  background: 'rgba(236,72,153,0.25)',
                  border: '1px solid rgba(236,72,153,0.5)',
                  color: '#fbcfe8',
                  boxShadow: '0 0 6px rgba(236,72,153,0.5)',
                }}
              >
                {favorites.length}
              </span>
            </div>
            {favoritesExpanded
              ? <ChevronDown className="w-4 h-4" style={{ color: '#fbcfe8', filter: 'drop-shadow(0 0 4px rgba(236,72,153,0.8))' }} />
              : <ChevronRight className="w-4 h-4" style={{ color: '#fbcfe8', filter: 'drop-shadow(0 0 4px rgba(236,72,153,0.8))' }} />}
          </button>
          <AnimatePresence initial={false}>
            {favoritesExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="border-t border-pink-500/20 p-3">
                  {favoritesLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-5 h-5 text-pink-400 animate-spin" />
                    </div>
                  ) : favorites.length === 0 ? (
                    <div className="text-center py-5 px-2">
                      <Heart className="w-8 h-8 text-pink-400/40 mx-auto mb-2" />
                      <p className="text-xs text-muted-foreground">
                        {t('meals_page.ajoute_tes_repas_ou_exercices_preferes_i', 'Ajoute tes repas ou exercices préférés ici !')}
                      </p>
                    </div>
                  ) : (
                    <>
                      {favorites.length > 5 && (
                        <div className="relative mb-2">
                          <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                          <input
                            type="text"
                            value={favoritesSearch}
                            onChange={(e) => setFavoritesSearch(e.target.value)}
                            placeholder={t('meals_page.rechercher_un_favori', 'Rechercher un favori…')}
                            className="w-full pl-8 pr-3 py-1.5 text-xs rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-pink-500/40 placeholder:text-muted-foreground"
                            data-testid="meals-favorites-search"
                          />
                        </div>
                      )}
                      <div
                        className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1"
                        data-testid="meals-favorites-list"
                      >
                        {(() => {
                          const q = favoritesSearch.trim().toLowerCase();
                          const filtered = q
                            ? favorites.filter(f => ((f.custom_name || f.product_name || f.brand) || '').toLowerCase().includes(q))
                            : favorites;
                          if (filtered.length === 0) {
                            return (
                              <p className="text-xs text-muted-foreground text-center py-3">
                                {t('meals_page.aucun_resultat', 'Aucun résultat')}
                              </p>
                            );
                          }
                          return filtered.map(fav => {
                            const n = fav.nutrition_per_100g || {};
                            const adding = addingFavoriteId === (fav.id || fav.barcode);
                            const isHot = !!fav.is_hot;
                            const isEditing = editingFavBarcode === fav.barcode;
                            const displayName = fav.custom_name || fav.product_name || fav.brand;
                            const isRenamed = Boolean(fav.custom_name);
                            return (
                              <div
                                key={fav.barcode || fav.id}
                                className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                                  isHot
                                    ? 'bg-gradient-to-r from-orange-500/15 to-pink-500/10 border border-orange-500/30'
                                    : 'bg-card/60 hover:bg-card'
                                }`}
                                data-testid={`favorite-${fav.barcode}`}
                              >
                                {isHot && !isEditing && (
                                  <span
                                    className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-500/25 text-orange-300 font-bold flex items-center gap-0.5 flex-shrink-0"
                                    title={`Loggé ${fav.log_count_7d}× cette semaine`}
                                    data-testid={`favorite-hot-badge-${fav.barcode}`}
                                  >
                                    🔥 <span className="tabular-nums">{fav.log_count_7d}</span>
                                  </span>
                                )}
                                {isEditing ? (
                                  // Inline edit mode
                                  <>
                                    <input
                                      type="text"
                                      autoFocus
                                      value={editFavValue}
                                      maxLength={80}
                                      onChange={(e) => setEditFavValue(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') commitEditFav();
                                        else if (e.key === 'Escape') cancelEditFav();
                                      }}
                                      placeholder={language === 'fr' ? 'Ex. Mon déjeuner classique' : language === 'es' ? 'Ej. Mi desayuno' : 'e.g. My breakfast'}
                                      className="flex-1 min-w-0 px-2 py-1 text-xs rounded bg-background border border-pink-500/50 focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                                      data-testid={`favorite-rename-input-${fav.barcode}`}
                                    />
                                    <Button
                                      size="sm"
                                      onClick={commitEditFav}
                                      disabled={savingFavRename}
                                      className="h-7 px-2 bg-emerald-500 hover:bg-emerald-600 text-white"
                                      data-testid={`favorite-rename-save-${fav.barcode}`}
                                    >
                                      {savingFavRename ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={cancelEditFav}
                                      className="h-7 w-7 p-0"
                                      data-testid={`favorite-rename-cancel-${fav.barcode}`}
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </Button>
                                  </>
                                ) : (
                                  // Display mode
                                  <>
                                    <div className="flex-1 min-w-0">
                                      <p
                                        className="text-xs font-medium text-foreground truncate"
                                        title={isRenamed ? `${language === 'fr' ? 'Original' : 'Original'}: ${fav.product_name}` : displayName}
                                      >
                                        {displayName}
                                        {isRenamed && (
                                          <span
                                            className="ml-1.5 text-[9px] uppercase tracking-wider text-pink-400/70"
                                            title={`${language === 'fr' ? 'Nom original' : 'Original name'}: ${fav.product_name}`}
                                          >
                                            ✓ {language === 'fr' ? 'perso' : 'custom'}
                                          </span>
                                        )}
                                      </p>
                                      <p className="text-[10px] text-muted-foreground">
                                        {Math.round(n.calories || 0)} cal · {Math.round(n.protein || 0)}g prot
                                      </p>
                                    </div>
                                    <Button
                                      size="sm"
                                      onClick={() => logFavoriteAsMeal(fav)}
                                      disabled={adding}
                                      className="h-7 px-2 text-[10px] font-bold bg-pink-500 hover:bg-pink-600 text-white"
                                      data-testid={`favorite-log-${fav.barcode}`}
                                    >
                                      {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : (t('meals_page.logger', 'Logger'))}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => startEditFav(fav)}
                                      className="h-7 w-7 p-0 text-muted-foreground hover:text-pink-400"
                                      data-testid={`favorite-edit-${fav.barcode}`}
                                      title={language === 'fr' ? 'Renommer' : language === 'es' ? 'Renombrar' : 'Rename'}
                                    >
                                      <Pencil className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => deleteFavorite(fav)}
                                      className="h-7 w-7 p-0 text-muted-foreground hover:text-red-400"
                                      data-testid={`favorite-del-${fav.barcode}`}
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Iter. 403 — Full-text search across custom_name + foods names */}
        <div className="mb-4" data-testid="meal-history-search">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              type="text"
              value={historySearch}
              onChange={(e) => setHistorySearch(e.target.value)}
              placeholder={
                language === 'fr'
                  ? 'Rechercher dans l\'historique (ex: cheat meal, tacos…)'
                  : language === 'es'
                  ? 'Buscar en el historial (ej: cheat meal, tacos…)'
                  : 'Search history (e.g. cheat meal, tacos…)'
              }
              className="pl-9 pr-9 bg-secondary border-border h-10"
              data-testid="meal-history-search-input"
            />
            {historySearch && (
              <button
                type="button"
                onClick={() => setHistorySearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                aria-label={language === 'fr' ? 'Effacer' : language === 'es' ? 'Borrar' : 'Clear'}
                data-testid="meal-history-search-clear"
              >
                {searching
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <X className="w-3.5 h-3.5" />}
              </button>
            )}
          </div>
          {isSearching && !searching && (
            <p className="mt-1.5 text-[11px] uppercase tracking-wider text-muted-foreground" data-testid="meal-history-search-count">
              {meals.length === 0
                ? (language === 'fr'
                    ? `Aucun repas trouvé pour « ${historySearch.trim()} »`
                    : language === 'es'
                    ? `Ningún resultado para « ${historySearch.trim()} »`
                    : `No meals found for "${historySearch.trim()}"`)
                : (language === 'fr'
                    ? `${total} repas trouvé${total > 1 ? 's' : ''} pour « ${historySearch.trim()} »`
                    : language === 'es'
                    ? `${total} resultado${total > 1 ? 's' : ''} para « ${historySearch.trim()} »`
                    : `${total} meal${total > 1 ? 's' : ''} for "${historySearch.trim()}"`)}
            </p>
          )}
        </div>

        {meals.length === 0 ? (
          <div className="text-center py-20">
            <UtensilsCrossed className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground text-lg">
              {isSearching
                ? (language === 'fr'
                    ? `Aucun repas ne correspond à « ${historySearch.trim()} »`
                    : language === 'es'
                    ? `Ningún resultado para « ${historySearch.trim()} »`
                    : `No meals match "${historySearch.trim()}"`)
                : t('meals_page.aucun_repas_enregistre', 'Aucun repas enregistré')}
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="meals-accordion">
            {displayedMeals.map(({ dateKey, meals: dayMeals }) => {
              const isExpanded = !!expandedDays[dateKey];
              const totals = getDayTotals(dayMeals);
              return (
                <div key={dateKey} className="rounded-xl border border-border bg-card overflow-hidden" data-testid={`day-group-${dateKey}`}>
                  {/* Day Header — always visible */}
                  <button
                    onClick={() => toggleDay(dateKey)}
                    className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-secondary/30 transition-colors"
                    data-testid={`day-toggle-${dateKey}`}
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded
                        ? <ChevronDown className="w-4 h-4 text-primary shrink-0" />
                        : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                      }
                      <div className="text-left">
                        <p className={`text-sm font-semibold ${isExpanded ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {formatDayLabel(dateKey)}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {dayMeals.length} {t('meals_page.repas', 'repas')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-[11px]">
                      <span className={`font-bold ${proteinTargetMin && totals.protein >= proteinTargetMin ? 'text-amber-300' : 'text-primary'}`}>
                        {totals.protein.toFixed(0)}g prot
                      </span>
                      <span className="text-muted-foreground">{totals.calories.toFixed(0)} cal</span>
                    </div>
                  </button>

                  {/* Day-level protein Micro-Victory bar — orange glow when target hit */}
                  {proteinTargetMin > 0 && (() => {
                    const dayHit = totals.protein >= proteinTargetMin;
                    const pct = Math.min(100, (totals.protein / proteinTargetMin) * 100);
                    return (
                      <div className="px-4 pb-2 -mt-1" data-testid={`day-protein-bar-${dateKey}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[9px] uppercase tracking-widest text-muted-foreground">
                            {t('meals_page.vers_cible_poids_sante', 'Vers cible Poids Santé')}
                          </span>
                          {dayHit && (
                            <span className="text-[9px] uppercase tracking-widest text-amber-400 font-bold">
                              {t('meals_page.cible_atteinte', '✓ Cible atteinte')}
                            </span>
                          )}
                        </div>
                        <div className="h-1.5 rounded-full bg-zinc-800/80 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-700 ${
                              dayHit
                                ? 'bg-gradient-to-r from-orange-600 via-orange-500 to-amber-300'
                                : 'bg-zinc-600/70'
                            }`}
                            style={{
                              width: `${Math.max(pct, 3)}%`,
                              boxShadow: dayHit ? '0 0 10px rgba(251,146,60,0.55), 0 0 3px rgba(245,158,11,0.45)' : 'none',
                            }}
                          />
                        </div>
                      </div>
                    );
                  })()}

                  {/* Meals — collapsible */}
                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="border-t border-border divide-y divide-border/50">
                          {dayMeals.map((meal) => (
                            <div key={meal.meal_id} className="p-3 sm:p-4" data-testid={`meal-row-${meal.meal_id}`}>
                              {/* Top row: time + status + actions */}
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">{formatTime(meal.analysis_date)}</span>
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex items-center gap-1 border ${getStatusColor(meal.protein_status)}`}>
                                    {getStatusIcon(meal.protein_status)}
                                    {meal.protein_status === 'on_track' ? (t('meals_page.sur_la_bonne_voie', 'Sur la bonne voie'))
                                      : meal.protein_status === 'low' ? (t('meals_page.en_dessous', 'En dessous'))
                                      : (t('meals_page.au_dessus', 'Au-dessus'))}
                                  </span>
                                  {meal.manually_edited && (
                                    <span className="text-[9px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">{t('meals_page.modifie', 'Modifie')}</span>
                                  )}
                                  <FatSecretBadge meal={meal} variant="mini" language={language} />
                                </div>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`meal-actions-${meal.meal_id}`}>
                                      <MoreVertical className="w-3.5 h-3.5" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="bg-card border-border">
                                    <DropdownMenuItem onClick={() => duplicateMealToday(meal)} className="cursor-pointer text-emerald-400 focus:text-emerald-300">
                                      <RefreshCw className={`w-3.5 h-3.5 mr-2 ${duplicatingId === meal.meal_id ? 'animate-spin' : ''}`} />
                                      {t('meals_page.remanger_aujourd_hui', "Remanger aujourd'hui")}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openCopyDialog(meal)} className="cursor-pointer">
                                      <CalendarDays className="w-3.5 h-3.5 mr-2" />
                                      {t('meals_page.copier_vers', 'Copier vers...')}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => saveMealAsFavorite(meal)}
                                      className={`cursor-pointer ${isMealFavorited(meal) ? 'text-pink-400 focus:text-pink-300' : ''}`}
                                      data-testid={`meal-favorite-${meal.meal_id}`}
                                    >
                                      <Heart className={`w-3.5 h-3.5 mr-2 ${isMealFavorited(meal) ? 'fill-pink-400 text-pink-400' : ''}`} />
                                      {isMealFavorited(meal)
                                        ? (t('meals_page.dans_mes_favoris', 'Dans mes favoris'))
                                        : (t('meals_page.sauvegarder_en_favori', 'Sauvegarder en favori'))}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openEditDialog(meal)} className="cursor-pointer">
                                      <Edit3 className="w-3.5 h-3.5 mr-2" /> {t('meals_page.modifier', 'Modifier')}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => openRenameDialog(meal)}
                                      className="cursor-pointer"
                                      data-testid={`meal-rename-${meal.meal_id}`}
                                    >
                                      <Pencil className="w-3.5 h-3.5 mr-2" />
                                      {language === 'fr' ? 'Renommer' : language === 'es' ? 'Renombrar' : 'Rename'}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openDeleteDialog(meal)} className="cursor-pointer text-destructive focus:text-destructive">
                                      <Trash2 className="w-3.5 h-3.5 mr-2" /> {t('meals_page.supprimer', 'Supprimer')}
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>

                              {/* Macros row */}
                              <div className="grid grid-cols-3 gap-2 mb-2">
                                <div className="bg-secondary/50 py-1.5 rounded text-center">
                                  <p className="text-[10px] text-muted-foreground">Prot.</p>
                                  <p className="text-sm font-bold text-primary">{safeNumber(meal.total_protein_g).toFixed(0)}g</p>
                                </div>
                                <div className="bg-secondary/50 py-1.5 rounded text-center">
                                  <p className="text-[10px] text-muted-foreground">Cal.</p>
                                  <p className="text-sm font-bold text-foreground">{safeNumber(meal.total_calories).toFixed(0)}</p>
                                </div>
                                <div className="bg-secondary/50 py-1.5 rounded text-center">
                                  <p className="text-[10px] text-muted-foreground">Carbs</p>
                                  <p className="text-sm font-bold text-foreground">{safeNumber(meal.total_carbs_g).toFixed(0)}g</p>
                                </div>
                              </div>

                              {/* Iter. 402 — Custom name banner (shown when user has renamed this meal) */}
                              {meal.custom_name && (
                                <div
                                  className="flex items-center gap-1.5 mb-2 px-2 py-1 rounded bg-pink-500/10 border border-pink-500/30"
                                  data-testid={`meal-custom-name-${meal.meal_id}`}
                                >
                                  <Pencil className="w-3 h-3 text-pink-400 shrink-0" />
                                  <span className="text-xs font-semibold text-pink-300 truncate" title={meal.custom_name}>
                                    {meal.custom_name}
                                  </span>
                                </div>
                              )}

                              {/* Foods list */}
                              <div className="space-y-1 mb-2">
                                {meal.foods_detected?.slice(0, 4).map((food, i) => (
                                  <div key={i} className="flex justify-between text-xs">
                                    <span className="text-muted-foreground truncate">{food.name}</span>
                                    <span className="text-muted-foreground/60 shrink-0 ml-2">{safeNumber(food.protein_g).toFixed(1)}g</span>
                                  </div>
                                ))}
                                {meal.foods_detected?.length > 4 && (
                                  <p className="text-[10px] text-muted-foreground/50">+{meal.foods_detected.length - 4} {t('meals_page.autres', 'autres')}</p>
                                )}
                              </div>

                              {/* Chef's Touch */}
                              {meal.chef_touch_suggestion && (
                                <div className="flex items-start gap-2 text-xs text-primary bg-primary/5 p-2 rounded mt-2">
                                  <ChefHat className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                  <p className="line-clamp-2">{meal.chef_touch_suggestion}</p>
                                </div>
                              )}

                              {/* Direct "Re-log today" action — one tap to remanger */}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => duplicateMealToday(meal)}
                                disabled={duplicatingId === meal.meal_id}
                                className="w-full mt-2 h-8 text-xs text-emerald-400 border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/15 hover:text-emerald-300"
                                data-testid={`meal-relog-${meal.meal_id}`}
                              >
                                {duplicatingId === meal.meal_id
                                  ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                                  : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                                {t('meals_page.remanger_aujourd_hui', "Remanger aujourd'hui")}
                              </Button>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
            {visibleDays < groupedMeals.length && (
              <div
                ref={lazySentinelRef}
                className="py-4 text-center text-xs text-muted-foreground"
                data-testid="meals-lazy-sentinel"
              >
                {t('meals_page.chargement', 'Chargement…')}
              </div>
            )}
          </div>
        )}

        {/* Edit Meal Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
                <Edit3 className="w-5 h-5 text-primary" />
                {t('meals_page.modifier_le_repas', 'Modifier le repas')}
              </DialogTitle>
              <DialogDescription>
                {t('meals_page.modifie_les_ingredients_et_recalcule_les', 'Modifie les ingredients et recalcule les macros')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {/* Meal Type selector — allow reclassification (Collation ↔ Dîner…) */}
              <div>
                <label className="text-sm font-semibold mb-2 block">
                  {t('meals_page.type_de_repas', 'Type de repas')}
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5" data-testid="edit-meal-type-group">
                  {[
                    { value: 'dejeuner', label_fr: 'Déjeuner',  label_en: 'Breakfast' },
                    { value: 'diner',    label_fr: 'Dîner',     label_en: 'Lunch' },
                    { value: 'souper',   label_fr: 'Souper',    label_en: 'Dinner' },
                    { value: 'collation',label_fr: 'Collation', label_en: 'Snack' },
                    { value: 'repas',    label_fr: 'Repas',     label_en: 'Meal' },
                  ].map(opt => {
                    const active = (editForm.meal_type || 'repas') === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setEditForm({ ...editForm, meal_type: opt.value })}
                        className={`h-9 rounded-md text-xs font-bold uppercase tracking-wide transition-colors ${
                          active
                            ? 'bg-primary text-primary-foreground ring-1 ring-primary'
                            : 'bg-secondary text-foreground/80 hover:bg-secondary/80'
                        }`}
                        data-testid={`edit-meal-type-${opt.value}`}
                      >
                        {t(`meals_page.meal_type_${opt.value}`, opt.label_fr)}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="border-t border-border" />
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold">{t('meals_page.ingredients', 'Ingredients')}</label>
                  <Button size="sm" variant="ghost" onClick={() => setEditIngredients(prev => [...prev, { id: Date.now(), name: '', portion_g: 100, unit: 'g', protein_g: 0, calories: 0, carbs_g: 0, fats_g: 0, fiber_g: 0 }])} className="h-7 text-xs text-primary" data-testid="add-ingredient-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> {t('meals_page.ajouter', 'Ajouter')}
                  </Button>
                </div>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {editIngredients.map((ing, idx) => (
                    <div key={ing.id} className="flex items-center gap-1.5 bg-secondary/50 rounded-lg p-1.5" data-testid={`ingredient-row-${idx}`}>
                      <Input value={ing.name} onChange={(e) => { const u = [...editIngredients]; u[idx] = { ...u[idx], name: e.target.value }; setEditIngredients(u); }} placeholder={t('meals_page.nom', 'Nom...')} className="flex-1 h-8 text-xs bg-background/50 border-border" />
                      <Input type="number" value={ing.portion_g} onChange={(e) => { const u = [...editIngredients]; u[idx] = { ...u[idx], portion_g: parseFloat(e.target.value) || 0 }; setEditIngredients(u); }} className="w-16 h-8 text-xs text-center bg-background/50 border-border" />
                      <button
                        type="button"
                        onClick={() => cycleIngredientUnit(idx)}
                        title={t('meals_page.change_unit', 'Changer l\\\'unité (g / ml / pièce)')}
                        data-testid={`edit-ingredient-unit-toggle-${idx}`}
                        className="h-8 min-w-[42px] px-2 rounded-md ring-1 ring-primary/40 bg-primary/10 hover:bg-primary/20 text-primary text-[10px] font-heading uppercase tracking-wider transition-colors"
                      >
                        {unitLabel(ing.unit)}
                      </button>
                      <Button size="sm" variant="ghost" onClick={() => setEditIngredients(prev => prev.filter((_, i) => i !== idx))} className="h-7 w-7 p-0 text-red-500/70 shrink-0"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  ))}
                </div>
                <Button onClick={handleRecalculateAI} disabled={recalculating || editIngredients.length === 0} className="w-full mt-2 bg-gradient-to-r from-primary to-orange-600" data-testid="recalculate-ai-btn">
                  {recalculating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('meals_page.recalcul', 'Recalcul...')}</> : <><Sparkles className="w-4 h-4 mr-2" /> {t('meals_page.recalculer_macros_ia', 'Recalculer macros (IA)')}</>}
                </Button>
              </div>
              <div className="border-t border-border" />
              <div>
                <label className="text-sm font-semibold mb-2 block">{t('meals_page.macros_totaux', 'Macros totaux')}</label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><label className="text-[10px] text-muted-foreground">{t('meals_page.prot_g', 'Prot (g)')}</label><Input type="number" value={editForm.total_protein_g} onChange={(e) => setEditForm({...editForm, total_protein_g: parseFloat(e.target.value) || 0})} className="bg-secondary border-border h-9" /></div>
                  <div className="space-y-1"><label className="text-[10px] text-muted-foreground">Calories</label><Input type="number" value={editForm.total_calories} onChange={(e) => setEditForm({...editForm, total_calories: parseFloat(e.target.value) || 0})} className="bg-secondary border-border h-9" /></div>
                  <div className="space-y-1"><label className="text-[10px] text-muted-foreground">{t('meals_page.glucides_g', 'Glucides (g)')}</label><Input type="number" value={editForm.total_carbs_g} onChange={(e) => setEditForm({...editForm, total_carbs_g: parseFloat(e.target.value) || 0})} className="bg-secondary border-border h-9" /></div>
                  <div className="space-y-1"><label className="text-[10px] text-muted-foreground">{t('meals_page.lipides_g', 'Lipides (g)')}</label><Input type="number" value={editForm.total_fats_g} onChange={(e) => setEditForm({...editForm, total_fats_g: parseFloat(e.target.value) || 0})} className="bg-secondary border-border h-9" /></div>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1"><X className="w-4 h-4 mr-2" /> {t('meals_page.annuler', 'Annuler')}</Button>
                <Button onClick={handleSaveEdit} disabled={saving} className="flex-1 bg-primary text-primary-foreground" data-testid="save-meal-edit-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {t('meals_page.sauvegarder', 'Sauvegarder')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent className="bg-card border-border">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading text-xl">{t('meals_page.supprimer_ce_repas', 'Supprimer ce repas?')}</AlertDialogTitle>
              <AlertDialogDescription>{t('meals_page.cette_action_est_irreversible', 'Cette action est irreversible.')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-border">{t('meals_page.annuler', 'Annuler')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteMeal} disabled={deleting} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete-meal-btn">
                {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                {t('meals_page.supprimer', 'Supprimer')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Iter. 402 — Rename history meal Dialog (Iter. 405.f extracted to component) */}
        <RenameMealDialog
          open={showRenameDialog}
          onClose={() => {
            setShowRenameDialog(false);
            setMealToRename(null);
            setRenameValue('');
          }}
          value={renameValue}
          onChange={setRenameValue}
          saving={savingMealRename}
          onSave={handleSaveRename}
          mealHasCustomName={!!mealToRename?.custom_name}
          language={language}
          t={t}
        />

        {/* Copy to specific date Dialog (Iter. 405.f extracted to component) */}
        <CopyMealToDateDialog
          meal={copyDialogMeal}
          onClose={() => setCopyDialogMeal(null)}
          targetDate={copyTargetDate}
          onTargetDateChange={setCopyTargetDate}
          saving={copySaving}
          onConfirm={handleCopyToDate}
          maxDate={getLocalDateString()}
          t={t}
        />
      </div>
    </Layout>
  );
};

export default Meals;
